/**
 * Dealer Map Component
 *
 * @package
 */

import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

const DealerMap = ( { vehicleBrand, selectedDealer, onDealerSelect, onMarkerClick } ) => {
	const mapRef = useRef( null );
	const mapInstanceRef = useRef( null );
	const markersRef = useRef( [] );
	const infoWindowsRef = useRef( [] );
	const markerInfoWindowMapRef = useRef( {} );
	const [ hasGoogleMaps, setHasGoogleMaps ] = useState( false );
	const [ mapsLoaded, setMapsLoaded ] = useState( false );

	useEffect( () => {
		const hasApiKey = window.atConfigurator?.hasGoogleMaps || false;

		if ( ! hasApiKey ) {
			setHasGoogleMaps( false );
			return;
		}

		if ( window.google && window.google.maps ) {
			setHasGoogleMaps( true );
			setMapsLoaded( true );
			return;
		}

		const checkGoogleMaps = setInterval( () => {
			if ( window.google && window.google.maps ) {
				setHasGoogleMaps( true );
				setMapsLoaded( true );
				clearInterval( checkGoogleMaps );
			}
		}, 100 );

		setTimeout( () => {
			clearInterval( checkGoogleMaps );
			if ( ! window.google || ! window.google.maps ) {
				setHasGoogleMaps( false );
			}
		}, 5000 );

		return () => {
			clearInterval( checkGoogleMaps );
		};
	}, [] );

	useEffect( () => {
		if ( ! hasGoogleMaps || ! mapsLoaded || ! window.google || ! mapRef.current ) {
			return;
		}

		// Snazzy Maps Ultra Light style (with labels, darker greys)
		const mapStyles = [
			{ featureType: 'water', elementType: 'geometry', stylers: [ { color: '#d0d0d0' }, { lightness: 10 } ] },
			{ featureType: 'landscape', elementType: 'geometry', stylers: [ { color: '#e0e0e0' }, { lightness: 10 } ] },
			{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [ { color: '#ffffff' }, { lightness: 10 } ] },
			{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [ { color: '#ffffff' }, { lightness: 20 }, { weight: 0.2 } ] },
			{ featureType: 'road.arterial', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 10 } ] },
			{ featureType: 'road.local', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 8 } ] },
			{ featureType: 'poi', elementType: 'geometry', stylers: [ { color: '#e0e0e0' }, { lightness: 12 } ] },
			{ featureType: 'poi.park', elementType: 'geometry', stylers: [ { color: '#d0d0d0' }, { lightness: 12 } ] },
			{ elementType: 'labels.text.stroke', stylers: [ { visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 } ] },
			{ elementType: 'labels.text.fill', stylers: [ { saturation: 36 }, { color: '#333333' }, { lightness: 40 } ] },
			{ elementType: 'labels.icon', stylers: [ { visibility: 'off' } ] },
			{ featureType: 'transit', elementType: 'geometry', stylers: [ { color: '#e5e5e5' }, { lightness: 12 } ] },
			{ featureType: 'administrative', elementType: 'geometry.fill', stylers: [ { color: '#f0f0f0' }, { lightness: 12 } ] },
			{ featureType: 'administrative', elementType: 'geometry.stroke', stylers: [ { color: '#f0f0f0' }, { lightness: 10 }, { weight: 1.2 } ] },
		];

		// Opening view only; the map refits itself to the dealer markers once they load.
		const { lat, lng, zoom } = window.atConfigurator.mapDefaults;
		const map = new window.google.maps.Map( mapRef.current, {
			zoom,
			center: { lat, lng },
			styles: mapStyles,
		} );

		mapInstanceRef.current = map;

		if ( vehicleBrand ) {
			loadDealerships( vehicleBrand );
		}

		return () => {
			markersRef.current.forEach( marker => {
				marker.setMap( null );
			} );
			markersRef.current = [];
			infoWindowsRef.current.forEach( infoWindow => {
				infoWindow.close();
			} );
			infoWindowsRef.current = [];
			markerInfoWindowMapRef.current = {};
		};
	}, [ vehicleBrand, hasGoogleMaps, mapsLoaded ] );

	useEffect( () => {
		if ( ! mapInstanceRef.current || markersRef.current.length === 0 || ! selectedDealer ) {
			return;
		}

		const dealerData = markerInfoWindowMapRef.current[ selectedDealer ];
		if ( dealerData ) {
			const { marker, infoWindow } = dealerData;

			markersRef.current.forEach( m => {
				const isSelected = selectedDealer === m.dealerId;
				if ( isSelected ) {
					m.setIcon( {
						path: window.google.maps.SymbolPath.CIRCLE,
						scale: 12,
						fillColor: '#0066cc',
						fillOpacity: 1,
						strokeColor: '#ffffff',
						strokeWeight: 3,
					} );
				} else {
					m.setIcon( {
						path: window.google.maps.SymbolPath.CIRCLE,
						scale: 12,
						fillColor: '#1e1e1e',
						fillOpacity: 1,
						strokeColor: '#ffffff',
						strokeWeight: 2,
					} );
				}
			} );

			infoWindowsRef.current.forEach( iw => {
				if ( iw !== infoWindow ) {
					iw.close();
				}
			} );

			infoWindow.open( mapInstanceRef.current, marker );
			mapInstanceRef.current.panTo( marker.getPosition() );
		} else {
			markersRef.current.forEach( marker => {
				const isSelected = selectedDealer === marker.dealerId;
				if ( isSelected ) {
					marker.setIcon( {
						path: window.google.maps.SymbolPath.CIRCLE,
						scale: 12,
						fillColor: '#0066cc',
						fillOpacity: 1,
						strokeColor: '#ffffff',
						strokeWeight: 3,
					} );
				} else {
					marker.setIcon( {
						path: window.google.maps.SymbolPath.CIRCLE,
						scale: 12,
						fillColor: '#1e1e1e',
						fillOpacity: 1,
						strokeColor: '#ffffff',
						strokeWeight: 2,
					} );
				}
			} );
		}
	}, [ selectedDealer ] );

	const loadDealerships = async brandId => {
		try {
			const response = await apiFetch( {
				path: `/wp/v2/dealerships?brand=${ brandId }&per_page=100`,
			} );

			if ( Array.isArray( response ) && response.length > 0 ) {
				displayDealerships( response );
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Failed to load dealerships:', error );
		}
	};

	const displayDealerships = dealerships => {
		if ( ! mapInstanceRef.current ) {
			return;
		}

		markersRef.current.forEach( marker => {
			marker.setMap( null );
		} );
		infoWindowsRef.current.forEach( infoWindow => {
			infoWindow.close();
		} );
		markersRef.current = [];
		infoWindowsRef.current = [];
		markerInfoWindowMapRef.current = {};

		const bounds = new window.google.maps.LatLngBounds();

		dealerships.forEach( dealership => {
			const location = dealership.acf?.location;
			if ( ! location || ! location.lat || ! location.lng ) {
				return;
			}

			const position = {
				lat: parseFloat( location.lat ),
				lng: parseFloat( location.lng ),
			};

			const isSelected = selectedDealer === dealership.id;
			const marker = new window.google.maps.Marker( {
				position,
				map: mapInstanceRef.current,
				title: dealership.title?.rendered || 'Dealership',
				icon: {
					path: window.google.maps.SymbolPath.CIRCLE,
					scale: 12,
					fillColor: isSelected ? '#0066cc' : '#1e1e1e',
					fillOpacity: 1,
					strokeColor: '#ffffff',
					strokeWeight: isSelected ? 3 : 2,
				},
			} );

			marker.dealerId = dealership.id;

			const infoWindow = new window.google.maps.InfoWindow( {
				content: createInfoWindowContent( dealership ),
			} );

			infoWindowsRef.current.push( infoWindow );

			markerInfoWindowMapRef.current[ dealership.id ] = {
				marker,
				infoWindow,
			};

			marker.addListener( 'click', () => {
				infoWindowsRef.current.forEach( iw => {
					if ( iw !== infoWindow ) {
						iw.close();
					}
				} );
				infoWindow.open( mapInstanceRef.current, marker );
				if ( onDealerSelect ) {
					onDealerSelect( dealership.id );
				}
				if ( onMarkerClick ) {
					onMarkerClick( dealership.id );
				}
			} );

			markersRef.current.push( marker );
			bounds.extend( position );
		} );

		if ( markersRef.current.length > 0 ) {
			mapInstanceRef.current.fitBounds( bounds );
		}
	};

	const createInfoWindowContent = dealership => {
		return `
			<div class="dealer-info-window">
				<h3>${ dealership.title?.rendered || 'Dealership' }</h3>
			</div>
		`;
	};

	if ( ! hasGoogleMaps ) {
		return (
			<div className="configurator-dealer-map-fallback" style={ { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px solid #ddd' } }>
				<div style={ { textAlign: 'center', padding: '2rem' } }>
					<p style={ { marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold' } }>
						{ __( 'Google Maps API Key Required', 'at-vehicle-configurator' ) }
					</p>
					<p style={ { marginBottom: '0.5rem', color: '#666' } }>
						{ __( 'Please configure the Google Maps API key in', 'at-vehicle-configurator' ) }
					</p>
					<p style={ { color: '#666', fontStyle: 'italic' } }>
						{ __( 'ACF Options → Google Maps API', 'at-vehicle-configurator' ) }
					</p>
				</div>
			</div>
		);
	}

	return <div ref={ mapRef } className="configurator-dealer-map" style={ { width: '100%' } } />;
};

export default DealerMap;
